/*
 * A resident's medicines, on their page.
 *
 * For the pharmacy this is where the work happens: add, change and stop, each saved as a draft;
 * approve them once the trays are packed; print the seals and the backup MAR; and answer what the
 * home has reported. For the home it is the approved list, read-only, and what they have told the
 * pharmacy — never the pharmacy's drafts, which the database keeps from them anyway.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { ApprovalDialog, DraftLine, Traits } from './approval.tsx';
import { moment, shortDay } from './days.ts';
import { MedicineForm } from './medicine-form.tsx';
import type { FormMode } from './medicine-form.tsx';
import type { CycleDates, Draft, Medicine, Report } from './medicine-types.ts';

type MedicineRecord = {
  pharmacy: boolean;
  homeId: string;
  supplyMode: 'tray' | 'original';
  allergies: string[];
  medicines: Medicine[];
  drafts: Draft[];
  reports: Report[];
  seals: { lastPrintedAt: string | null; reprintNeeded: boolean } | null;
  cycle: CycleDates | null;
  today: string;
};

type Props = {
  residentId: string;
  residentName: string;
  onPrintSeals: () => void;
  onPrintMar: () => void;
};

export function ResidentMedicines({ residentId, residentName, onPrintSeals, onPrintMar }: Props) {
  const [data, setData] = useState<MedicineRecord | null>(null);
  const [form, setForm] = useState<{ mode: FormMode; reported?: Report } | null>(null);
  const [approving, setApproving] = useState(false);
  const [closingReport, setClosingReport] = useState<string | null>(null);
  const [closeNote, setCloseNote] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await api.get<MedicineRecord>(`/api/residents/${residentId}/medicine-record`));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load their medicines.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const discard = async (draft: Draft) => {
    const what = draft.kind === 'new' ? draft.name : draft.medicineName || draft.name;
    if (!window.confirm(`Throw away the draft for ${what}? It is kept on the record as thrown away.`)) return;
    try {
      await api.post(`/api/changes/${draft.id}/discard`);
      setMessage('Draft thrown away.');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  };

  const closeReport = async (reportId: string) => {
    try {
      await api.post(`/api/reports/${reportId}/done`, { note: closeNote });
      setClosingReport(null);
      setCloseNote('');
      setMessage('Marked as dealt with.');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  };

  /* Acting on what the home reported: the right form, with the medicine already chosen. */
  const actOn = (report: Report) => {
    const medicine = data?.medicines.find((m) => m.id === report.medicineId);
    if (report.what === 'new' || !medicine) return setForm({ mode: { kind: 'new' }, reported: report });
    if (medicine.draft) {
      const draft = data?.drafts.find((d) => d.id === medicine.draft?.id);
      if (draft) return setForm({ mode: { kind: 'edit', draft }, reported: report });
    }
    return setForm({ mode: { kind: report.what === 'stopped' ? 'stop' : 'change', medicine }, reported: report });
  };

  if (!data) {
    return (
      <section className="meds">
        <h2>Medicines</h2>
        {error ? <p className="error" role="alert">{error}</p> : <p className="muted">Loading…</p>}
      </section>
    );
  }

  const pharmacy = data.pharmacy;
  const newDrafts = data.drafts.filter((d) => d.kind === 'new');
  const openReports = data.reports.filter((r) => r.status === 'open');
  const doneReports = data.reports.filter((r) => r.status === 'done');
  const draftById = new Map(data.drafts.map((d) => [d.id, d]));

  return (
    <section className="meds">
      <div className="head">
        <h2>Medicines</h2>
        <div className="row-actions">
          {pharmacy ? <button type="button" onClick={() => setForm({ mode: { kind: 'new' } })}>Add a medicine</button> : null}
          {pharmacy && data.supplyMode === 'tray' ? (
            <button type="button" className="quiet" onClick={onPrintSeals}>Print seals</button>
          ) : null}
          <button type="button" className="quiet" onClick={onPrintMar}>Print backup MAR</button>
        </div>
      </div>

      {message ? <p className="notice" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}

      {pharmacy && data.drafts.length ? (
        <div className="drafts-bar">
          <p>
            <strong>{data.drafts.length} change{data.drafts.length === 1 ? '' : 's'} not on the home's chart yet.</strong>{' '}
            They are drafts until approved.
          </p>
          <button type="button" onClick={() => setApproving(true)}>Review and approve</button>
        </div>
      ) : null}

      {pharmacy && data.seals?.reprintNeeded ? (
        <div className="notice warn">
          <p>
            A tray medicine has changed since their seals were printed
            {data.seals.lastPrintedAt ? ` on ${moment(data.seals.lastPrintedAt)}` : ''}. Reprint so the seal matches the chart.
          </p>
          <button type="button" onClick={onPrintSeals}>Reprint seals</button>
        </div>
      ) : null}

      {openReports.length ? (
        <div className="reports">
          <h3>{pharmacy ? 'The home has reported' : 'Sent to the pharmacy'}</h3>
          <ul className="list">
            {openReports.map((report) => (
              <li key={report.id}>
                <div className="row static column report-line">
                  <div className="row-line">
                    <span>
                      <span className="chip warn">{report.whatLabel}</span>{' '}
                      <span className="row-main">{report.medicineName || (report.what === 'new' ? 'A new medicine' : '')}</span>
                    </span>
                    <span className="muted small">{report.reportedBy} · {moment(report.reportedAt)}</span>
                  </div>
                  <p className="small">"{report.detail}"{report.sourceDetail ? ` — ${report.sourceDetail}` : ''}</p>
                  {pharmacy ? (
                    closingReport === report.id ? (
                      <div className="row-actions">
                        <input
                          aria-label="How it was dealt with"
                          value={closeNote}
                          onChange={(e) => setCloseNote(e.target.value)}
                          placeholder="No change needed: GP confirmed the dose stays the same"
                        />
                        <button type="button" disabled={closeNote.trim().length < 3} onClick={() => void closeReport(report.id)}>
                          Mark as dealt with
                        </button>
                        <button type="button" className="quiet" onClick={() => setClosingReport(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div className="row-actions">
                        {report.what !== 'other' ? (
                          <button type="button" onClick={() => actOn(report)}>Make the change</button>
                        ) : null}
                        <button type="button" className="quiet" onClick={() => setClosingReport(report.id)}>
                          Close without a change
                        </button>
                      </div>
                    )
                  ) : (
                    <p className="muted small">Waiting for the pharmacy. The round shows it against the medicine until then.</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {pharmacy && newDrafts.length ? (
        <>
          <h3>Being added</h3>
          <ul className="list">
            {newDrafts.map((draft) => (
              <li key={draft.id}>
                <DraftLine draft={draft} />
                <div className="row-actions under">
                  <button type="button" className="quiet" onClick={() => setForm({ mode: { kind: 'edit', draft } })}>Edit draft</button>
                  <button type="button" className="quiet" onClick={() => void discard(draft)}>Throw away</button>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{pharmacy ? 'On the chart' : 'On their chart'}</h3>
      {!data.medicines.length ? <p className="muted">Nothing on their chart yet.</p> : null}
      <ul className="list">
        {data.medicines.map((medicine) => {
          const draft = medicine.draft ? draftById.get(medicine.draft.id) : undefined;
          return (
            <li key={medicine.id}>
              <div className={`row static column${draft ? ' has-draft' : ''}`}>
                <div className="row-line">
                  <span className="row-main">{medicine.name}</span>
                  <span className="muted small">
                    {medicine.startsOn > data.today ? `from ${shortDay(medicine.startsOn)}` : ''}
                    {medicine.endsOn ? ` until ${shortDay(medicine.endsOn)}` : ''}
                  </span>
                </div>
                <p className="small">
                  {medicine.directions}
                  {medicine.schedule.length ? <span className="muted"> · {medicine.schedule.join('; ')}</span> : null}
                </p>
                <Traits item={medicine} />

                {pharmacy && draft ? (
                  <div className="draft-note">
                    <p className="small">
                      <strong>Draft: {draft.kind === 'stop' ? 'stopping' : 'changing'}</strong>
                      {draft.startsOn ? ` from ${shortDay(draft.startsOn)}` : ''}
                      {/* The name as well as the directions: a new strength is often the whole change. */}
                      {draft.kind === 'stop' ? ` — ${draft.stopReason}` : ` to ${draft.name}: ${draft.directions}`}
                    </p>
                    <div className="row-actions">
                      <button type="button" className="quiet" onClick={() => setForm({ mode: { kind: 'edit', draft } })}>Edit draft</button>
                      <button type="button" className="quiet" onClick={() => void discard(draft)}>Throw away</button>
                    </div>
                  </div>
                ) : pharmacy ? (
                  <div className="row-actions">
                    <button type="button" className="quiet" onClick={() => setForm({ mode: { kind: 'change', medicine } })}>Change</button>
                    <button type="button" className="quiet" onClick={() => setForm({ mode: { kind: 'stop', medicine } })}>Stop</button>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {doneReports.length ? (
        <details className="reports-done">
          <summary>{doneReports.length} reported change{doneReports.length === 1 ? '' : 's'} dealt with</summary>
          <ul className="list">
            {doneReports.map((report) => (
              <li key={report.id} className="small">
                <strong>{report.whatLabel}{report.medicineName ? `, ${report.medicineName}` : ''}</strong> — "{report.detail}".{' '}
                <span className="muted">
                  Reported by {report.reportedBy} {moment(report.reportedAt)}; {report.doneNote.toLowerCase()} by {report.doneBy}
                  {report.doneAt ? ` ${moment(report.doneAt)}` : ''}.
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {form ? (
        <MedicineForm
          homeId={data.homeId}
          residentId={residentId}
          residentName={residentName}
          allergies={data.allergies}
          mode={form.mode}
          reported={form.reported ?? null}
          cycle={data.cycle}
          today={data.today}
          onClose={() => setForm(null)}
          onDone={() => {
            setForm(null);
            setMessage('Saved as a draft. The home will see it once it is approved.');
            void load();
          }}
        />
      ) : null}

      {approving ? (
        <ApprovalDialog
          homeId={data.homeId}
          residentId={residentId}
          onClose={() => setApproving(false)}
          onDone={(approved) => {
            setApproving(false);
            setMessage(`${approved} change${approved === 1 ? '' : 's'} approved. They are on the home's chart now.`);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}
